/**
 * CloudWaitress Booking Widget
 *
 * A standalone script that renders a floating booking button on any website.
 * When clicked, it toggles the restaurant's booking flow above the button.
 * Press-and-drag moves the icon; it stays within screen edge margins.
 * Opening the form does not move the icon — the panel is placed separately
 * and clamped so it stays fully on-screen.
 *
 * Usage:
 *   <script src="https://yourstore.domain/widget.js" async></script>
 */

(function () {
	const TAG = "[cw-booking-widget]";

	// Prevent widget from running inside an iframe (avoids cascade loops)
	if (window !== window.top) return;
	// Prevent duplicate widget on same page
	if (document.getElementById("cw-booking-widget")) return;

	// currentScript is null when the host site loads the snippet as a module or
	// re-runs it from a cached blob, so fall back to finding our own tag. Every
	// bail-out below logs, because a widget that vanishes without a trace is the
	// hardest kind of install problem to support.
	const script = (document.currentScript ||
		document.querySelector("script[data-booking-url]") ||
		document.querySelector('script[src*="widget.js"]')) as HTMLScriptElement | null;
	if (!script) {
		console.error(`${TAG} could not find its own <script> tag, so the widget did not load.`);
		return;
	}

	const baseUrl = new URL(script.src).origin;
	const primaryColor = script.getAttribute("data-primary-color") || "#a85d44";
	const position = script.getAttribute("data-position") || "bottom-right";
	const buttonStyle = script.getAttribute("data-button-style") || "rounded";
	const welcomeMessage =
		script.getAttribute("data-welcome-message") || "Book a table";
	const embedTag = (script.getAttribute("data-tag") || "").trim();
	// Optional UI language for the form (e.g. "fr", "zht"). Shape-checked here; the storefront
	// server only honours codes it has a translation bundle for.
	const lang = (script.getAttribute("data-lang") || "").trim().toLowerCase();
	const explicitUrl = script.getAttribute("data-booking-url");
	const url = explicitUrl ? new URL(explicitUrl) : new URL(baseUrl);
	if (!explicitUrl) {
		url.pathname = "/reserve";
	}
	url.searchParams.set("widget", "1");
	url.searchParams.set("parent_origin", window.location.origin);
	if (embedTag) {
		url.searchParams.set("tag", embedTag);
	}
	if (/^[a-z]{2,3}$/.test(lang)) {
		url.searchParams.set("lang", lang);
	}
	const bookingUrl = url.toString();

	const BUTTON_SIZE = buttonStyle === "pill" ? 148 : 58;
	const Z_INDEX = 2147483000;
	const EDGE_MARGIN = 24;
	const DRAG_THRESHOLD = 8;

	/** Dock to nearest edge with fixed margin — can slide along edges, not float in center. */
	function dockToEdge(left: number, bottom: number, width: number, height: number) {
		const margin = EDGE_MARGIN;
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const maxLeft = Math.max(margin, vw - width - margin);
		const maxBottom = Math.max(margin, vh - height - margin);
		const clampedLeft = Math.min(Math.max(left, margin), maxLeft);
		const clampedBottom = Math.min(Math.max(bottom, margin), maxBottom);

		const top = vh - clampedBottom - height;
		const right = vw - clampedLeft - width;
		const distLeft = clampedLeft;
		const distRight = right;
		const distTop = top;
		const distBottom = clampedBottom;
		const nearest = Math.min(distLeft, distRight, distTop, distBottom);

		if (nearest === distLeft) {
			return { left: margin, bottom: clampedBottom };
		}
		if (nearest === distRight) {
			return { left: maxLeft, bottom: clampedBottom };
		}
		if (nearest === distTop) {
			return { left: clampedLeft, bottom: maxBottom };
		}
		return { left: clampedLeft, bottom: margin };
	}

	function createWidget() {
		const container = document.createElement("div");
		container.id = "cw-booking-widget";
		const isMobileDevice = window.innerWidth < 768;
		Object.assign(container.style, {
			position: "fixed",
			bottom: isMobileDevice ? "80px" : "24px",
			[position === "bottom-left" ? "left" : "right"]: "24px",
			zIndex: String(Z_INDEX),
			display: "flex",
			flexDirection: "column",
			alignItems: position === "bottom-left" ? "flex-start" : "flex-end",
			gap: "10px",
			fontFamily:
				'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
			touchAction: "none",
			pointerEvents: "none",
		});

		let customPos: { left: number; bottom: number } | null = null;

		function applyContainerPos() {
			if (!customPos) return;
			container.style.left = `${customPos.left}px`;
			container.style.bottom = `${customPos.bottom}px`;
			container.style.right = "auto";
			container.style.top = "auto";
		}

		/** Reclamp using launcher container only — form panel is separate. */
		function reclamp() {
			if (!customPos) return;
			requestAnimationFrame(() => {
				customPos = dockToEdge(
					customPos!.left,
					customPos!.bottom,
					container.offsetWidth,
					container.offsetHeight
				);
				applyContainerPos();
				if (panel.style.display !== "none") layoutPanel();
			});
		}

		/**
		 * Place the form near the icon without moving the icon.
		 * Prefers above; flips below if needed; clamps fully on-screen.
		 */
		function layoutPanel() {
			const m = EDGE_MARGIN;
			const gap = 10;
			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const maxW = Math.min(360, Math.max(240, vw - m * 2));
			const maxH = Math.min(560, Math.max(220, vh - m * 2));
			const rect = btnWrap.getBoundingClientRect();

			let left = rect.left < vw / 2 ? rect.left : rect.right - maxW;
			left = Math.min(Math.max(left, m), Math.max(m, vw - maxW - m));

			const spaceAbove = rect.top - m - gap;
			const spaceBelow = vh - rect.bottom - m - gap;
			let top: number;
			let height: number;

			if (spaceAbove >= 240 || spaceAbove >= spaceBelow) {
				height = Math.min(maxH, Math.max(220, spaceAbove));
				top = rect.top - gap - height;
				if (top < m) {
					top = m;
					height = Math.max(200, rect.top - gap - m);
				}
			} else {
				top = rect.bottom + gap;
				height = Math.min(maxH, Math.max(220, spaceBelow));
				if (top + height > vh - m) {
					height = Math.max(200, vh - m - top);
				}
			}

			if (top < m) top = m;
			if (left < m) left = m;
			if (left + maxW > vw - m) left = Math.max(m, vw - maxW - m);
			if (top + height > vh - m) height = Math.max(180, vh - m - top);
			height = Math.min(height, vh - m * 2);

			Object.assign(panel.style, {
				left: `${left}px`,
				top: `${top}px`,
				width: `${maxW}px`,
				height: `${height}px`,
				right: "auto",
				bottom: "auto",
			});
		}

		const bubble = document.createElement("div");
		bubble.textContent = welcomeMessage;
		Object.assign(bubble.style, {
			maxWidth: "220px",
			padding: "10px 14px",
			borderRadius: "18px",
			backgroundColor: "#fff",
			color: "#0f172a",
			fontSize: "13px",
			fontWeight: "700",
			lineHeight: "1.35",
			boxShadow: "0 12px 28px rgba(15,23,42,0.16)",
			border: "1px solid rgba(226,232,240,0.95)",
			pointerEvents: "auto",
		});

		// Floating button
		const btn = document.createElement("button");
		btn.setAttribute("aria-label", "Book a table");
		const radius =
			buttonStyle === "square"
				? "18px"
				: buttonStyle === "pill"
					? "999px"
					: "50%";
		Object.assign(btn.style, {
			width: `${BUTTON_SIZE}px`,
			height: "58px",
			borderRadius: radius,
			backgroundColor: primaryColor,
			border: "none",
			cursor: "grab",
			boxShadow: `0 12px 30px -10px ${primaryColor}aa`,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: "8px",
			color: "#fff",
			fontSize: "12px",
			fontWeight: "800",
			letterSpacing: "0.08em",
			textTransform: "uppercase",
			transition: "transform 0.2s ease, box-shadow 0.2s ease",
			touchAction: "none",
			userSelect: "none",
			pointerEvents: "auto",
		});
		btn.innerHTML =
			buttonStyle === "pill"
				? `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg><span>Reserve</span>`
				: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`;

		btn.addEventListener("mouseenter", () => {
			if (dragState) return;
			btn.style.transform = "translateY(-2px) scale(1.04)";
			btn.style.boxShadow = `0 18px 38px -10px ${primaryColor}cc`;
		});
		btn.addEventListener("mouseleave", () => {
			btn.style.transform = "scale(1)";
			btn.style.boxShadow = `0 12px 30px -10px ${primaryColor}aa`;
		});

		// Panel is fixed & separate from the icon container so opening never moves the icon.
		const panel = document.createElement("div");
		Object.assign(panel.style, {
			display: "none",
			position: "fixed",
			zIndex: String(Z_INDEX + 1),
			boxSizing: "border-box",
			padding: "10px",
			borderRadius: "28px",
			overflow: "visible",
			backgroundColor: "#fff",
			boxShadow: "0 28px 64px rgba(15,23,42,0.24)",
			border: "1px solid rgba(255,255,255,0.54)",
			opacity: "0",
			transform: "translateY(8px)",
			transition: "opacity 0.18s ease, transform 0.18s ease",
		});

		// Close button inside panel
		const closeBtn = document.createElement("button");
		closeBtn.setAttribute("aria-label", "Close booking");
		Object.assign(closeBtn.style, {
			position: "absolute",
			top: "12px",
			right: "12px",
			zIndex: "10",
			width: "32px",
			height: "32px",
			borderRadius: "50%",
			border: "none",
			background: "rgba(15,23,42,0.08)",
			color: "#334155",
			fontSize: "18px",
			fontWeight: "600",
			lineHeight: "1",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		});
		closeBtn.textContent = "\u00D7";
		closeBtn.addEventListener("click", () => closeBooking());

		// Backdrop overlay
		const overlay = document.createElement("div");
		Object.assign(overlay.style, {
			display: "none",
			position: "fixed",
			inset: "0",
			zIndex: String(Z_INDEX - 1),
			background: "rgba(15,23,42,0.46)",
			backdropFilter: "blur(2px)",
		});
		overlay.addEventListener("click", () => {
			// Overlay blocks page clicks but does not close the form.
			// Form closes only via the "x" button.
		});

		const frame = document.createElement("iframe");
		// `src` is assigned on first open (see openBooking) so host page views that never touch
		// the widget do not load the booking form. `loading="lazy"` would not help: browsers load
		// hidden iframes eagerly.
		frame.setAttribute("allow", "payment");
		frame.setAttribute("scrolling", "no");
		frame.setAttribute("title", "Table booking");
		Object.assign(frame.style, {
			width: "100%",
			height: "100%",
			border: "none",
			borderRadius: "20px",
			display: "block",
			backgroundColor: "#fff",
		});

		// Shown when the booking form never reports itself ready — almost always a
		// host-site Content-Security-Policy blocking the frame. A blocked frame still
		// fires "load" with an empty document, so the form pings us instead and the
		// absence of that ping is what we act on.
		const fallback = document.createElement("div");
		Object.assign(fallback.style, {
			display: "none",
			position: "absolute",
			inset: "10px",
			borderRadius: "20px",
			backgroundColor: "#fff",
			padding: "28px 22px",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			textAlign: "center",
			gap: "14px",
			color: "#0f172a",
		});
		const fallbackText = document.createElement("p");
		fallbackText.textContent = "The booking form could not be displayed on this page.";
		Object.assign(fallbackText.style, {
			margin: "0",
			fontSize: "14px",
			fontWeight: "700",
			lineHeight: "1.45",
		});
		const fallbackLink = document.createElement("a");
		fallbackLink.href = bookingUrl;
		fallbackLink.target = "_blank";
		fallbackLink.rel = "noopener";
		fallbackLink.textContent = "Open booking page";
		Object.assign(fallbackLink.style, {
			display: "inline-block",
			padding: "11px 20px",
			borderRadius: "999px",
			backgroundColor: primaryColor,
			color: "#fff",
			fontSize: "13px",
			fontWeight: "800",
			letterSpacing: "0.06em",
			textTransform: "uppercase",
			textDecoration: "none",
		});
		fallback.appendChild(fallbackText);
		fallback.appendChild(fallbackLink);

		let frameReady = false;
		let frameRequested = false;
		let readyTimer: number | undefined;

		function showFallback() {
			if (frameReady) return;
			frame.style.display = "none";
			fallback.style.display = "flex";
			console.error(
				`${TAG} the booking form at ${url.origin} did not load. If this site sends a ` +
					`Content-Security-Policy, it must allow ${url.origin} in frame-src.`
			);
		}

		panel.appendChild(closeBtn);
		panel.appendChild(frame);
		panel.appendChild(fallback);

		function handleMessage(event: MessageEvent) {
			if (event.origin !== url.origin) return;
			const data = event.data as {
				type?: string;
				booking_id?: string;
				url?: string;
			};
			if (!data) return;
			if (data.type === "cw:booking:ready") {
				frameReady = true;
				window.clearTimeout(readyTimer);
				frame.style.display = "block";
				fallback.style.display = "none";
				return;
			}
			if (data.type !== "cw:booking:created" || !data.booking_id) return;
			// The iframe navigates the top window itself after a booking (the access token never
			// crosses origins). This message only arrives as a fallback when that navigation was
			// blocked, so land on the token-less booking page; the guest has the emailed manage link.
			closeBooking();
			window.location.href = data.url || `${url.origin}/booking/${data.booking_id}`;
		}

		function closeBooking() {
			window.clearTimeout(readyTimer);
			panel.style.opacity = "0";
			panel.style.transform = "translateY(8px)";
			overlay.style.display = "none";
			bubble.style.display = "";
			dismiss.style.display = "flex";
			btn.setAttribute("aria-expanded", "false");
			setTimeout(() => {
				panel.style.display = "none";
			}, 180);
		}

		function openBooking() {
			if (!frameRequested) {
				frameRequested = true;
				frame.src = bookingUrl;
			}
			if (!frameReady) {
				window.clearTimeout(readyTimer);
				readyTimer = window.setTimeout(showFallback, 6000);
			}
			layoutPanel();
			panel.style.display = "block";
			overlay.style.display = "block";
			bubble.style.display = "none";
			dismiss.style.display = "none";
			btn.setAttribute("aria-expanded", "true");

			requestAnimationFrame(() => {
				layoutPanel();
				panel.style.opacity = "1";
				panel.style.transform = "translateY(0)";
			});
		}

		function toggleBooking() {
			if (panel.style.display !== "none") {
				closeBooking();
			} else {
				openBooking();
			}
		}

		let dragState: {
			pointerId: number;
			startX: number;
			startY: number;
			origLeft: number;
			origBottom: number;
			moved: boolean;
			allowDrag: boolean;
		} | null = null;

		function onPointerDown(e: PointerEvent) {
			if (e.button !== 0 && e.pointerType === "mouse") return;
			const panelOpen = panel.style.display !== "none";
			const rect = container.getBoundingClientRect();
			const pos = customPos || {
				left: rect.left,
				bottom: window.innerHeight - rect.bottom,
			};
			customPos = pos;
			applyContainerPos();
			dragState = {
				pointerId: e.pointerId,
				startX: e.clientX,
				startY: e.clientY,
				origLeft: pos.left,
				origBottom: pos.bottom,
				moved: false,
				allowDrag: !panelOpen,
			};
			btn.style.cursor = panelOpen ? "pointer" : "grabbing";
			btnWrap.setPointerCapture(e.pointerId);
			e.preventDefault();
		}

		function onPointerMove(e: PointerEvent) {
			if (!dragState || dragState.pointerId !== e.pointerId || !dragState.allowDrag) return;
			const dx = e.clientX - dragState.startX;
			const dy = e.clientY - dragState.startY;
			if (!dragState.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
			dragState.moved = true;
			customPos = dockToEdge(
				dragState.origLeft + dx,
				dragState.origBottom - dy,
				container.offsetWidth,
				container.offsetHeight
			);
			applyContainerPos();
		}

		function onPointerUp(e: PointerEvent) {
			if (!dragState || dragState.pointerId !== e.pointerId) return;
			const moved = dragState.moved;
			dragState = null;
			btn.style.cursor = "grab";
			try {
				btnWrap.releasePointerCapture(e.pointerId);
			} catch {
				// ignore
			}
			if (!moved) {
				// Tap opens/closes form. Icon stays put. Overlay does not close; form "x" also closes.
				toggleBooking();
			} else {
				reclamp();
			}
		}

		btn.setAttribute("aria-expanded", "false");
		window.addEventListener("message", handleMessage);
		window.addEventListener("resize", reclamp);

		// Dismiss button (×) — positioned inside widget button
		const dismiss = document.createElement("button");
		dismiss.setAttribute("aria-label", "Dismiss widget");
		Object.assign(dismiss.style, {
			position: "absolute",
			top: "-4px",
			right: "-4px",
			width: "18px",
			height: "18px",
			borderRadius: "50%",
			border: "none",
			background: "rgba(0,0,0,0.55)",
			color: "#fff",
			fontSize: "11px",
			fontWeight: "700",
			lineHeight: "1",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "0",
			pointerEvents: "auto",
		});
		dismiss.textContent = "\u00D7";
		dismiss.addEventListener("click", (e) => {
			e.stopPropagation();
			window.clearTimeout(readyTimer);
			container.remove();
			overlay.remove();
			panel.remove();
			window.removeEventListener("message", handleMessage);
			window.removeEventListener("resize", reclamp);
		});
		dismiss.addEventListener("pointerdown", (e) => {
			e.stopPropagation();
		});

		const btnWrap = document.createElement("div");
		btnWrap.id = "cw-booking-widget-btn";
		Object.assign(btnWrap.style, {
			position: "relative",
			touchAction: "none",
			pointerEvents: "auto",
		});
		btnWrap.appendChild(btn);
		btnWrap.appendChild(dismiss);
		btnWrap.addEventListener("pointerdown", onPointerDown);
		btnWrap.addEventListener("pointermove", onPointerMove);
		btnWrap.addEventListener("pointerup", onPointerUp);
		btnWrap.addEventListener("pointercancel", onPointerUp);

		container.appendChild(bubble);
		container.appendChild(btnWrap);
		document.body.appendChild(overlay);
		document.body.appendChild(panel);
		document.body.appendChild(container);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", createWidget);
	} else {
		createWidget();
	}
})();
